import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Send, LifeBuoy, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "How do I join the iOS beta?",
  "Is Fun On The Run live in my city?",
  "How do push alerts work?",
  "I'm having trouble logging into the app",
  "I want to become an ambassador",
];

const INITIAL_GREETING = "Hey — FOTR Support here. Ask me anything about the app, the beta, joining a city, partnerships, or how to book time with Danny.";

export default function SupportChat() {
  const [messages, setMessages] = useState([
    { role: "assistant", content: INITIAL_GREETING },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  const resetToStart = () => {
    setMessages([{ role: "assistant", content: INITIAL_GREETING }]);
    setSessionId(null);
    setInput("");
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setSending(true);
    try {
      const res = await axios.post(`${API}/support/chat`, {
        session_id: sessionId,
        message: msg,
      });
      setSessionId(res.data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Hmm, I couldn't connect just now. Try again in a sec — or email us at fotr@funontherun.co and we'll jump in.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="bg-surface border border-line rounded-4xl shadow-xl overflow-hidden flex flex-col h-[480px] max-h-[min(480px,calc(100vh-120px))]"
      data-testid="support-chat-widget"
    >
      <div className="px-5 py-4 border-b border-line flex items-center gap-3 bg-surface-alt">
        <div className="w-9 h-9 rounded-xl bg-secondary text-ink flex items-center justify-center">
          <LifeBuoy size={18} />
        </div>
        <div>
          <div className="font-display font-black text-lg leading-none">FOTR Support</div>
          <div className="text-xs text-muted mt-0.5">Real answers · usually within seconds</div>
        </div>
        <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-muted">
          <span className="w-2 h-2 rounded-full bg-green-500 pulse-dot" /> Online
        </span>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-3" data-testid="support-messages">
        {messages.map((m, i) => (
          <Bubble key={i} role={m.role} text={m.content} />
        ))}
        {sending && <Bubble role="assistant" text="…" typing />}

        {messages.length === 1 && !sending && (
          <div className="pt-2 flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                onClick={() => send(s)}
                className="text-xs font-medium px-3 py-2 rounded-full border border-line bg-white hover:border-secondary hover:text-ink transition-colors"
                data-testid={`support-suggestion-${s.slice(0, 10).replace(/\s+/g, "-").toLowerCase()}`}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {messages.length > 2 && !sending && (
          <div className="pt-1 flex justify-center">
            <button
              type="button"
              onClick={resetToStart}
              className="text-[11px] font-semibold px-3 py-1.5 rounded-full border border-line bg-white text-muted hover:text-secondary hover:border-secondary transition-colors"
              data-testid="support-reset"
            >
              ← Back to main choices
            </button>
          </div>
        )}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
        className="p-3 border-t border-line bg-white flex items-center gap-2"
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask a quick question..."
          className="flex-1 px-4 py-3 rounded-2xl bg-surface-alt border border-transparent focus:border-secondary focus:outline-none text-sm"
          data-testid="support-input"
          disabled={sending}
        />
        <button
          type="submit"
          disabled={sending || !input.trim()}
          className="w-12 h-12 rounded-2xl bg-secondary text-ink flex items-center justify-center disabled:opacity-50 hover:bg-secondary/80 transition-colors"
          data-testid="support-send-button"
          aria-label="Send"
        >
          <Send size={18} />
        </button>
      </form>
    </div>
  );
}

function Bubble({ role, text, typing }) {
  const isUser = role === "user";
  const clean = !isUser && text
    ? text
        .replace(/\*\*(.+?)\*\*/g, "$1")
        .replace(/\*(.+?)\*/g, "$1")
        .replace(/^#+\s+/gm, "")
        .replace(/^\s*[-+*]\s+/gm, "• ")
    : text;
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-ink text-white rounded-br-md"
            : "bg-surface-alt text-ink rounded-bl-md"
        }`}
      >
        {typing ? (
          <span className="inline-flex gap-1 py-1">
            <span className="w-1.5 h-1.5 rounded-full bg-muted animate-bounce" style={{ animationDelay: "0ms" }} />
            <span className="w-1.5 h-1.5 rounded-full bg-muted animate-bounce" style={{ animationDelay: "120ms" }} />
            <span className="w-1.5 h-1.5 rounded-full bg-muted animate-bounce" style={{ animationDelay: "240ms" }} />
          </span>
        ) : (
          clean
        )}
      </div>
    </div>
  );
}

// Modal-style trigger used inside the footer
export function SupportChatTrigger({ buttonClassName, buttonLabel = "Chat with support", testid = "support-trigger" }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Click outside the popover closes it (desktop). Mobile bottom sheet uses backdrop.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        onClick={() => setOpen((v) => !v)}
        className={buttonClassName || "inline-flex items-center gap-2 bg-secondary text-ink px-5 py-3 rounded-full font-semibold hover:bg-secondary/90 transition-colors"}
        data-testid={testid}
      >
        <LifeBuoy size={18} />
        <span className="text-sm">{buttonLabel}</span>
      </button>

      {open && (
        <>
          {/* Mobile backdrop (only visible <md) */}
          <div
            className="md:hidden fixed inset-0 bg-ink/50 backdrop-blur-sm z-40"
            onClick={() => setOpen(false)}
            aria-hidden
          />

          {/* Chat popover — desktop anchors ABOVE the trigger button; mobile is bottom sheet */}
          <div
            className="
              z-50 text-ink
              max-md:fixed max-md:inset-x-0 max-md:bottom-0
              md:absolute md:bottom-full md:right-0 md:mb-3
              md:w-[420px]
              animate-in fade-in slide-in-from-bottom-2 duration-200
            "
            data-testid="support-modal"
            role="dialog"
            aria-label="FOTR Support Chat"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-end mb-2 md:mb-2 max-md:px-3 max-md:pt-3">
              <button
                onClick={() => setOpen(false)}
                className="w-8 h-8 rounded-full bg-white shadow flex items-center justify-center hover:bg-line transition-colors"
                data-testid="support-close-button"
                aria-label="Close support chat"
              >
                <X size={15} />
              </button>
            </div>
            <SupportChat />
          </div>
        </>
      )}
    </div>
  );
}
